<!--
  @description：用户登录、注册、登出、管理后台菜单
-->
<template>
  <div class="auth-menu">
    <NuxtLink v-if="isMounted && isAdmin" to="/admin" class="admin-link">管理后台</NuxtLink>

    <el-dropdown v-if="isMounted && isLoggedIn" trigger="click" @command="handleUserCommand">
      <el-button plain>
        {{ displayName }}
        <el-icon class="el-icon--right">
          <ArrowDown />
        </el-icon>
      </el-button>
      <template #dropdown>
        <el-dropdown-menu>
          <el-dropdown-item disabled>{{ user?.email }}</el-dropdown-item>
          <el-dropdown-item :command="AuthMenuCommand.PROFILE" divided>账号设置</el-dropdown-item>
          <el-dropdown-item :command="AuthMenuCommand.LOGOUT" divided>退出登录</el-dropdown-item>
        </el-dropdown-menu>
      </template>
    </el-dropdown>

    <el-button v-else-if="isMounted" type="primary" plain @click="openAuthDialog('login')">
      登录 / 注册
    </el-button>

    <el-dialog v-model="dialogVisible" :title="mode === 'login' ? '登录' : '注册'" width="380px" destroy-on-close>
      <el-form label-position="top" @submit.prevent>
        <el-form-item v-if="mode === 'register'" label="昵称">
          <el-input v-model="form.name" placeholder="可选" />
        </el-form-item>
        <el-form-item label="邮箱">
          <el-input v-model="form.email" autocomplete="email" placeholder="you@example.com" />
        </el-form-item>
        <el-form-item label="密码">
          <el-input v-model="form.password" type="password" autocomplete="current-password" show-password
            @keyup.enter="submitAuth" />
        </el-form-item>
      </el-form>

      <div class="mode-switch">
        <el-button link type="primary" @click="toggleMode">
          {{ mode === 'login' ? '还没有账号？去注册' : '已有账号？去登录' }}
        </el-button>
      </div>

      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="submitting" @click="submitAuth">
          {{ mode === 'login' ? '登录' : '注册' }}
        </el-button>
      </template>
    </el-dialog>

    <el-dialog v-model="profileDialogVisible" title="账号设置" width="420px" destroy-on-close>
      <el-form label-position="top" @submit.prevent>
        <el-form-item label="邮箱">
          <el-input :model-value="user?.email || ''" disabled />
        </el-form-item>
        <el-form-item label="昵称">
          <el-input v-model="profileForm.name" maxlength="40" show-word-limit placeholder="可选" clearable />
        </el-form-item>
        <el-form-item label="当前密码">
          <el-input v-model="profileForm.currentPassword" type="password" autocomplete="current-password" show-password
            placeholder="修改密码时必填" clearable />
        </el-form-item>
        <el-form-item label="新密码">
          <el-input v-model="profileForm.newPassword" type="password" autocomplete="new-password" show-password
            placeholder="不修改则留空" @keyup.enter="submitProfile" clearable />
        </el-form-item>
        <el-form-item label="确认新密码">
          <el-input v-model="profileForm.confirmPassword" type="password" autocomplete="new-password" show-password
            placeholder="再次输入新密码" @keyup.enter="submitProfile" clearable />
        </el-form-item>
      </el-form>

      <template #footer>
        <el-button @click="profileDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="profileSubmitting" @click="submitProfile">
          保存
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from "vue";
import { ArrowDown } from "@element-plus/icons-vue";
import { ElMessage } from "element-plus";
import { AuthMenuCommand, StorageKey } from "~/enums";

const { user, isLoggedIn, isAdmin, login, register, updateProfile, logout } = useAuthUser();
const { getAnonymousId } = useAnonymousUser();

const dialogVisible = ref(false);
const profileDialogVisible = ref(false);
const isMounted = ref(false);
const submitting = ref(false);
const profileSubmitting = ref(false);
const mode = ref<"login" | "register">("login");
const form = reactive({
  name: "",
  email: "",
  password: "",
});
const profileForm = reactive({
  name: "",
  currentPassword: "",
  newPassword: "",
  confirmPassword: "",
});

onMounted(() => {
  isMounted.value = true;
});

const displayName = computed(() => user.value?.name || user.value?.email || "已登录");

/**
 * 获取匿名用户ID
 */
const getStoredAnonymousId = () => {
  if (!import.meta.client) return "";
  const cookies = document.cookie.split("; ");
  const targetCookie = cookies.find((item) => item.startsWith(`${StorageKey.ANONYMOUS_USER_ID}=`));
  return targetCookie?.split("=")[1] || getAnonymousId();
};

/**
 * 打开授权对话框
 */
const openAuthDialog = (nextMode: "login" | "register" = "login") => {
  mode.value = nextMode;
  dialogVisible.value = true;
};

/**
 * 切换登录/注册模式
 */
const toggleMode = () => {
  mode.value = mode.value === "login" ? "register" : "login";
};

/**
 * 打开账号设置
 */
const openProfileDialog = () => {
  profileForm.name = user.value?.name || "";
  profileForm.currentPassword = "";
  profileForm.newPassword = "";
  profileForm.confirmPassword = "";
  profileDialogVisible.value = true;
};

/**
 * 提交授权
 */
const submitAuth = async () => {
  if (!form.email.trim() || !form.password) {
    ElMessage.warning("请输入邮箱和密码");
    return;
  }

  submitting.value = true;
  try {
    const payload = {
      email: form.email,
      password: form.password,
      name: form.name,
      anonymousUserId: getStoredAnonymousId(),
    };

    if (mode.value === "login") {
      await login(payload);
    } else {
      await register(payload);
    }

    dialogVisible.value = false;
    ElMessage.success(mode.value === "login" ? "登录成功" : "注册成功");
    await refreshNuxtData();
  } catch (error: any) {
    ElMessage.error(error?.statusMessage || error?.data?.statusMessage || "操作失败");
  } finally {
    submitting.value = false;
  }
};

/**
 * 提交账号资料
 */
const submitProfile = async () => {
  const shouldChangePassword = !!(
    profileForm.currentPassword ||
    profileForm.newPassword ||
    profileForm.confirmPassword
  );

  if (shouldChangePassword) {
    if (!profileForm.currentPassword || !profileForm.newPassword) {
      ElMessage.warning("请输入当前密码和新密码");
      return;
    }
    if (profileForm.newPassword.length < 6) {
      ElMessage.warning("新密码至少需要 6 位");
      return;
    }
    if (profileForm.newPassword !== profileForm.confirmPassword) {
      ElMessage.warning("两次输入的新密码不一致");
      return;
    }
  }

  profileSubmitting.value = true;
  try {
    await updateProfile({
      name: profileForm.name,
      currentPassword: shouldChangePassword ? profileForm.currentPassword : undefined,
      newPassword: shouldChangePassword ? profileForm.newPassword : undefined,
    });

    profileDialogVisible.value = false;
    ElMessage.success("账号信息已更新");
    await refreshNuxtData();
  } catch (error: any) {
    ElMessage.error(error?.statusMessage || error?.data?.statusMessage || "保存失败");
  } finally {
    profileSubmitting.value = false;
  }
};

/**
 * 处理用户命令
 */
const handleUserCommand = async (command: string) => {
  if (command === AuthMenuCommand.PROFILE) {
    openProfileDialog();
    return;
  }
  if (command !== AuthMenuCommand.LOGOUT) return;

  await logout();
  ElMessage.success("已退出登录");
  await navigateTo("/");
};

defineExpose({
  openAuthDialog,
});
</script>

<style scoped lang="scss">
.auth-menu {
  display: flex;
  align-items: center;
  gap: 12px;
  white-space: nowrap;
}

.admin-link {
  color: #409eff;
  font-size: 14px;
  text-decoration: none;
}

.mode-switch {
  display: flex;
  justify-content: flex-start;
}
</style>
